/**
 * A4 — Lista de lo que toca facturar (cuotas de iguala, hitos, presupuestos aceptados y
 * tiempos sin facturar), con el emisor con el que saldría cada factura y el botón para
 * crear el borrador. Lo usan la pantalla Facturación → Pendientes y la ficha del cliente.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, CalendarClock, Loader2, Receipt } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { useAuth } from '../../contexts/AuthContext'
import {
  IssuerViaLabel,
  PendingKindLabel,
  type PendingBillingItem,
  type PendingBillingResponse,
} from '@toga/shared'

interface Props {
  /** Solo lo de este cliente (en la ficha). Sin él, todo el bufete. */
  clientId?: string
  /** Oculta la columna del cliente (en la ficha ya se sabe). */
  hideClient?: boolean
  /** Avisa de los totales cuando se cargan (para tarjetas y títulos). */
  onLoaded?: (r: PendingBillingResponse) => void
}

/** Endpoint que crea el borrador de cada origen; todos devuelven `{ invoiceId }`. */
function emitRequest(item: PendingBillingItem): { path: string; body?: unknown } | null {
  switch (item.kind) {
    case 'MILESTONE':
      return { path: '/milestones/invoice', body: { ids: [item.sourceId] } }
    case 'QUOTE':
      return { path: `/quotes/${item.sourceId}/to-invoice` }
    case 'RECURRING':
      return { path: `/recurring-invoices/${item.sourceId}/generate-now` }
    case 'TIME':
      return null
  }
}

function when(item: PendingBillingItem): string {
  const date = new Date(item.dueDate).toLocaleDateString('es-ES')
  if (item.kind === 'TIME') return `desde ${date}`
  if (item.upcoming) return `${date} · en ${item.daysUntil} d`
  return item.daysLate > 0 ? `${date} · ${item.daysLate} d de retraso` : `${date} · hoy`
}

export default function PendingBillingList({ clientId, hideClient, onLoaded }: Props) {
  const auth = useAuth()
  const navigate = useNavigate()
  const canInvoice =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const [data, setData] = useState<PendingBillingResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''
      const r = await api.get<PendingBillingResponse>(`/billing/pending${qs}`)
      setData(r)
      setError(null)
      onLoaded?.(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar')
    }
  }, [clientId, onLoaded])

  useEffect(() => {
    void load()
  }, [load])

  const emit = async (item: PendingBillingItem) => {
    const req = emitRequest(item)
    if (!req) return
    if (item.issuerProblem) {
      alert(item.issuerProblem)
      return
    }
    const who = item.issuerName ? `${item.issuerName} (${item.issuerNif})` : 'el emisor por defecto'
    if (
      !confirm(
        `Se creará un borrador de factura de «${item.label}» para ${item.clientName} con el emisor ${who}. Podrás revisarlo antes de emitir.`
      )
    ) {
      return
    }
    setBusy(item.key)
    setError(null)
    try {
      const r = await api.post<{ invoiceId: string }>(req.path, req.body)
      navigate(`/billing/${r.invoiceId}`)
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'No se pudo crear la factura'))
      setBusy(null)
      void load()
    }
  }

  if (!data && !error) {
    return (
      <div className="px-5 py-6 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="m-4 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="px-5 py-8 text-center t-muted text-sm">
          No hay nada pendiente de facturar. Aquí aparecen las cuotas de iguala, los hitos de pago y
          los presupuestos aceptados que aún no tienen factura, y los tiempos sin facturar.
        </div>
      )}
      {data && data.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider t-faint border-b border-base">
                <th className="px-5 py-2 font-medium">Concepto</th>
                {!hideClient && <th className="px-3 py-2 font-medium">Cliente</th>}
                <th className="px-3 py-2 font-medium">Cuándo</th>
                <th className="px-3 py-2 font-medium text-right">Base</th>
                <th className="px-3 py-2 font-medium">Se factura con</th>
                <th className="px-5 py-2 font-medium text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.key} className="border-b border-base/50 last:border-b-0 align-top">
                  <td className="px-5 py-2.5">
                    <div className="t-primary">{i.label}</div>
                    <div className="t-faint text-xs flex items-center gap-1.5">
                      {PendingKindLabel[i.kind]}
                      {i.caseCode && (
                        <Link
                          to={`/cases/${i.caseId}`}
                          className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-toga-500/15 text-toga-200 border border-toga-500/30 hover:underline"
                        >
                          {i.caseCode}
                        </Link>
                      )}
                    </div>
                  </td>
                  {!hideClient && (
                    <td className="px-3 py-2.5 t-secondary">
                      <Link
                        to={`/clients/${i.clientId}?tab=facturacion`}
                        className="hover:underline"
                      >
                        {i.clientName}
                      </Link>
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                    <span
                      className={
                        i.upcoming ? 't-muted' : i.daysLate > 0 ? 'text-amber-300' : 't-secondary'
                      }
                    >
                      {i.upcoming && <CalendarClock size={11} className="inline mr-1 -mt-0.5" />}
                      {when(i)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono t-primary whitespace-nowrap">
                    {formatCents(i.baseCents)}
                    {i.totalCents != null && (
                      <div className="t-faint text-[11px]">con IVA {formatCents(i.totalCents)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {i.issuerProblem ? (
                      <span
                        className="text-amber-300 flex items-start gap-1"
                        title={i.issuerProblem}
                      >
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" /> Revisar emisor
                      </span>
                    ) : (
                      <>
                        <div className="t-secondary">{i.issuerName}</div>
                        {i.issuerVia && (
                          <div className="t-faint">{IssuerViaLabel[i.issuerVia]}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right whitespace-nowrap">
                    {i.kind === 'TIME' ? (
                      <Link
                        to={`/cases/${i.sourceId}`}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-base t-secondary hover:bg-hover whitespace-nowrap"
                      >
                        Ver tiempos
                      </Link>
                    ) : (
                      canInvoice && (
                        <button
                          type="button"
                          disabled={!i.canEmit || busy !== null}
                          onClick={() => void emit(i)}
                          title={
                            i.canEmit
                              ? 'Crea un borrador de factura para revisarlo'
                              : 'Se factura primero la cuota más antigua'
                          }
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-toga-500/40 text-toga-200 hover:bg-toga-500/10 inline-flex items-center gap-1.5 disabled:opacity-40"
                        >
                          {busy === i.key ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Receipt size={12} />
                          )}
                          Facturar
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
